import { logger } from '@adornis/base/logging.js';
import { SetBehaviorSubject } from '@adornis/base/rxjs-extension/index.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MatchAPI } from '@adornis/bh-matching-matches/api/matchAPI.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { currentlySelectedTimeframe } from '@adornis/bh-matching-timeframes/components/matching-semester-selection.js';
import { MatchingUserAPI } from '@adornis/bh-matching-user/api/MatchingUserAPI.js';
import '@adornis/bh-matching-user/components/buddy-user-details.js';
import {
  BuddyUser,
  BuddyUserMentee,
  BuddyUserMentor,
  DefaultMenteeUser,
  DefaultMentorUser,
} from '@adornis/bh-matching-user/entities/buddy-user.js';
import '@adornis/buchungsheld-theme/components/tr-toggle-content.js';
import '@adornis/buchungsheld-theme/components/tr-vr.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { virtualize } from '@adornis/chemistry/directives/when-seen.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/chemistry/elements/components/x-loading-state';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/chemistry/elements/components/x-toggle-content';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { IntAspectSchema } from '@adornis/filter/aspect-schemas/IntAspect.js';
import '@adornis/filter/client/x-filter-editor';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import '@adornis/popover/x-dropdown-selection.js';
import '@adornis/popover/x-tooltip.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import type { TranslationDictionaryBuddy } from '../../api/translations.js';
import { AutoMatchUserEntry, autoMatchUsers } from '../api/auto-matching.js';
import { HiddenSortingMachingUserFields } from '../static/HiddenSortingMachingUserFields.js';

const searchFields = ['firstName', 'lastName', 'email'];

/**
 * @element buddy-page-matching
 */
@customElement('buddy-page-matching')
export class BuddyPageMatching extends ChemistryLitElement {
  @property({ attribute: false }) menteeUserClass: typeof BuddyUserMentee = DefaultMenteeUser;
  @property({ attribute: false }) mentorUserClass: typeof BuddyUserMentor = DefaultMentorUser;
  @state() private _mentorsOpen = true;
  @state() private _menteeOpen = true;

  protected _translator = new TranslationController<TranslationDictionaryBuddy>(this);
  @state() protected _currentlySelectedTimeframe = new RXController(this, currentlySelectedTimeframe);

  protected _personTogleComponentName = 'buddy-user-details';

  @state() private _showMentorSorting = false;
  private _mentorFilter = new AdornisFilter({ searchFields });

  protected mentorPagination = new SubscriptionPagination(
    (skip: number, limit: number, filter: AdornisFilter) => () =>
      this._currentlySelectedTimeframe.observable.pipe(
        switchMap(timeframe =>
          timeframe
            ? (MatchingUserAPI.subscribeAllInCurrentTimeframe(
                this.mentorUserClass._class,
                timeframe._id,
                filter,
                skip,
                limit,
              )(selectionSet(() => BuddyUserMentor, 3)) as Observable<BuddyUserMentor[]>)
            : of([]),
        ),
      ),
    [this._mentorFilter],
    selectionSet(() => this.mentorUserClass, 3),
  );

  protected totalMentors = new RXController(
    this,
    combineLatest([this._currentlySelectedTimeframe.observable, this._mentorFilter.whenChanging]).pipe(
      switchMap(async ([timeframe, filter]) => {
        if (!timeframe) return 0;
        const result = await MatchingUserAPI.getInCurrentTimeframeCount(
          this.mentorUserClass._class,
          timeframe._id,
          filter,
        )();
        return result;
      }),
    ),
  );

  @state() private _mentors = new RXController(this, this.mentorPagination.data);

  @state() private _showMenteeSorting = false;
  private _menteeFilter = new AdornisFilter({ searchFields });

  protected menteePagination = new SubscriptionPagination(
    (skip: number, limit: number, filter: AdornisFilter) => () =>
      this._currentlySelectedTimeframe.observable.pipe(
        switchMap(timeframe =>
          timeframe
            ? (MatchingUserAPI.subscribeAllInCurrentTimeframe(
                this.menteeUserClass._class,
                timeframe._id,
                filter,
                skip,
                limit,
              )(selectionSet(() => BuddyUserMentee, 3)) as Observable<BuddyUserMentee[]>)
            : of([]),
        ),
      ),
    [this._menteeFilter],
    selectionSet(() => this.menteeUserClass, 3),
  );

  protected totalMentees = new RXController(
    this,
    combineLatest([this._currentlySelectedTimeframe.observable, this._menteeFilter.whenChanging]).pipe(
      switchMap(async ([timeframe, filter]) => {
        if (!timeframe) return 0;
        const result = await MatchingUserAPI.getInCurrentTimeframeCount(
          this.menteeUserClass._class,
          timeframe._id,
          filter,
        )();
        return result;
      }),
    ),
  );

  @state() private _mentees = new RXController(this, this.menteePagination.data);
  @state() protected _matches = new RXController<Match[]>(this, []);

  private readonly _openPersons = new SetBehaviorSubject(new Set<string>());
  // @ts-expect-error just used to trigge rerenders on openPersons update
  @state() private readonly _openPersonsRender = new RXController(this, this._openPersons);

  @state() windowSize = new WindowSizeController(this);

  override connectedCallback(): void {
    super.connectedCallback();
    this._mentorFilter.whenChanging.subscribe(filter => {
      this.mentorPagination.updateParameters(filter);
    });
    this._menteeFilter.whenChanging.subscribe(filter => {
      this.menteePagination.updateParameters(filter);
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.mentorPagination.stop();
    this.menteePagination.stop();
  }

  override render() {
    if (this._mentors.isLoading || this._mentees.isLoading) return html` <x-loading-state></x-loading-state> `;

    const mentors = this._mentors.value ?? [];
    const mentees = this._mentees.value ?? [];
    const colWidth = this.windowSize.value?.width < 1200 ? '300px' : '400px';
    return html`
      <x-flex space="lg" h100 padding="md" ${css({ position: 'relative', paddingBottom: '0' })}>
        <x-flex horizontal space="md" crossaxis-center>
          <x-h1> Matching </x-h1>
        </x-flex>

        ${this._buttonBar(this._mentors.value ?? [], this._mentees.value ?? [])}
        <x-grid
          flex
          space="xs"
          columns="${this._menteeOpen ? colWidth : 'max-content'} 2px 3fr 2px ${this._mentorsOpen
            ? colWidth
            : 'max-content'}"
          rows="1em 1fr"
          ${css({ minHeight: '0' })}
        >
          <x-flex horizontal crossaxis-center space="xs" ${css({ cursor: 'pointer' })}>
            ${this._menteeOpen
              ? html`
                  <x-icon
                    ${css({ fontSize: '20px' })}
                    @click=${() => {
                      this._menteeOpen = !this._menteeOpen;
                    }}
                  >
                    chevron_left
                  </x-icon>
                `
              : nothing}
            <x-h3> ${this._menteeOpen ? html` Mentees (${this.totalMentees.value ?? 0}) ` : html` &nbsp; `} </x-h3>
          </x-flex>
          <div></div>
          <x-h3 ${css({ textAlign: 'center' })}> Matches </x-h3>
          <div></div>
          <x-flex horizontal crossaxis-center space="xs" ${css({ cursor: 'pointer' })}>
            ${this._mentorsOpen
              ? html`
                  <x-icon
                    ${css({ fontSize: '20px' })}
                    @click=${() => {
                      this._mentorsOpen = !this._mentorsOpen;
                    }}
                  >
                    chevron_right
                  </x-icon>
                `
              : nothing}
            <x-h3> ${this._mentorsOpen ? html` Mentoren (${this.totalMentors.value ?? 0}) ` : html` &nbsp; `} </x-h3>
          </x-flex>
          ${this._menteeOpen
            ? this._persons(mentees, BuddyUserMentee, this._menteeFilter, index => {
                this.virtualizationOnRender(index, this.menteePagination);
              })
            : this._emptyDiv('keyboard_arrow_left', () => (this._menteeOpen = true))}
          <tr-vr></tr-vr>
          ${this._matchesColumn()}
          <tr-vr></tr-vr>
          ${this._mentorsOpen
            ? this._persons(mentors, BuddyUserMentor, this._mentorFilter, index => {
                this.virtualizationOnRender(index, this.mentorPagination);
              })
            : this._emptyDiv('keyboard_arrow_right', () => (this._mentorsOpen = true))}
        </x-grid>
      </x-flex>
    `;
  }

  protected _buttonBar(mentors: BuddyUserMentor[], mentees: BuddyUserMentee[]) {
    const matches = this._matches.value;
    const notAllMatchesFilled = !matches.length || matches.some(m => !m.mentor || !m.mentee);

    return html`
      <x-flex horizontal space="md" end>
        <x-button
          mode="outline"
          icon="autorenew"
          @click=${async () => {
            const allFieldsForAutoMatching = [
              'genderPreference',
              'gender',
              'foreignLanguages',
              'motherTongue',
              'faculty',
            ];

            const autoMatches = await XNativeDialog.waitFor(this._matchUsers(mentors, mentees));
            this._matches.next(autoMatches);
            void XSnackbar.show(
              this._translator.translate('buddy', 'createMatchesSuccessful').replace('#', autoMatches.length + ''),
            );
          }}
        >
          Auto-Matching
        </x-button>
        <x-button
          ?disabled=${notAllMatchesFilled}
          @click=${async () => {
            try {
              if (notAllMatchesFilled) return;

              const confirm = await XDialog.confirm(this._translator.translate('buddy', 'createMatchesConfirm'));
              if (!confirm) return;

              const newMatches = matches.map(m => {
                return new Match({
                  mentorID: m.mentor._id,
                  menteeID: m.mentee._id,
                  timeframeID: this._currentlySelectedTimeframe.value?._id,
                });
              });
              await XDialog.waitFor(MatchAPI.create(newMatches)());

              this.requestUpdate();
              this._matches.next([]);
              void XSnackbar.show(this._translator.translate('buddy', 'createMatchesSuccessful'));
              void goTo('/matches');
            } catch (e) {
              logger.error(e, 'Something went wrong creating the matches.');
            }
          }}
        >
          ${this._translator.translate('buddy', 'createMatches')}
        </x-button>
      </x-flex>
    `;
  }

  private _emptyDiv(icon: string, clickAction: () => void) {
    return html`
      <x-flex
        center
        crossaxis-center
        @click=${() => clickAction()}
        ${acss({
          cursor: 'pointer',
          'x-icon': { transition: 'color .2s, transform .2s' },
          '&:hover x-icon': { color: this.colors.accent, transform: 'scale(1.5)' },
        })}
      >
        <x-icon ${css({ fontSize: this.spacing.lg })}> ${icon} </x-icon>
      </x-flex>
    `;
  }

  private _matchesColumn() {
    return html`
      <x-flex space="sm" ${css({ minHeight: '0' })}>
        ${this._createMatchBox()}
        <x-flex flex ${css({ minHeight: '0', overflowY: 'auto', paddingBottom: this.spacing.md })}>
          ${repeat(this._matches.value ?? [], match => this._matchDisplay(match))}
        </x-flex>
      </x-flex>
    `;
  }

  private _matchDisplay(match: Match) {
    return html`
      <x-card padding="sm" ${css({ width: '100%' })}>
        <x-flex horizontal end>
          <x-button
            icon="delete"
            mode="text"
            tone="error"
            @click=${async () => {
              if (!(await XNativeDialog.confirm('Vorläufiges Match auflösen?'))) return;
              this._matches.next(this._matches.value.filter(innerMatch => innerMatch !== match));
            }}
          >
            Match entfernen
          </x-button>
        </x-flex>
        <x-flex horizontal space="xs" w100>
          ${match.mentee ? this._personToggle(match.mentee, true) : this._fillMatchBox(match, 'mentee')}
          ${match.mentor ? this._personToggle(match.mentor, true) : this._fillMatchBox(match, 'mentor')}
        </x-flex>
      </x-card>
    `;
  }

  private _persons(
    users: BuddyUser[],
    userClass: typeof BuddyUserMentee | typeof BuddyUserMentor,
    filter: AdornisFilter,
    onReachEnd: (index: number) => void,
  ) {
    return html`
      <x-flex space="md" ${css({ overflowY: 'auto', paddingBottom: this.spacing.md })}>
        <x-flex space="xs">
          <x-hr></x-hr>
          <x-filter-editor
            .options=${buildFullOptions(
              {
                class: userClass,
                view: new View({ filter: filter }),
              },
              {
                searchable: true,
                fieldAspectGeneration: {
                  shouldIncludeField: (bqlFieldDef, path, baseEntity) =>
                    ![
                      '_class',
                      '_id',
                      'username',
                      'roles',
                      'isOnline',
                      'ios_sub',
                      'emailConfirmed',
                      'timeframeID',
                      'matches',
                    ].includes(bqlFieldDef.name),
                },
                overridesForIdentifier: {
                  numMatches: IntAspectSchema,
                },
              },
            ).filterOptions}
          ></x-filter-editor>
          <x-hr></x-hr>
          ${(userClass === BuddyUserMentee ? this._showMenteeSorting : this._showMentorSorting)
            ? html`
                <x-flex space="xs" ${css({ userSelect: 'none', cursor: 'pointer' })}>
                  <x-flex
                    horizontal
                    space="xs"
                    crossaxis-center
                    @click=${() => {
                      if (userClass === BuddyUserMentee) this._showMenteeSorting = false;
                      else this._showMentorSorting = false;
                    }}
                  >
                    <x-icon>keyboard_arrow_up</x-icon>
                    <x-text>${this._translator.translate('field', 'sortingHide')}</x-text>
                  </x-flex>
                  <x-flex horizontal space="xs" padding="xs" flex>
                    <x-dropdown-selection
                      flex
                      placeholder="${this._translator.translate('field', 'sortingChooseField')}"
                      .value=${filter.sortingFields[0]}
                      @value-picked=${(choiseEvt: ValueEvent<string>) => {
                        const choise = choiseEvt.detail.value;
                        if (!choise) return;

                        filter.sortingFields = [choiseEvt.detail.value];
                        filter.sortingDirections = ['asc'];
                      }}
                      .selectables=${Object.keys(
                        (userClass === this.menteeUserClass ? this.menteeUserClass : this.mentorUserClass).fields,
                      ).filter(curAttr => !HiddenSortingMachingUserFields.includes(curAttr))}
                      .renderString=${(fieldAttr: string) => this._translator.translate('field', fieldAttr)}
                    ></x-dropdown-selection>
                    <x-dropdown-selection
                      flex
                      placeholder="${this._translator.translate('field', 'sortingChooseDirection')}"
                      .value=${filter.sortingDirections[0] ?? 'asc'}
                      @value-picked=${(choiseEvt: ValueEvent<boolean>) => {
                        const choise = choiseEvt.detail.value;

                        filter.sortingDirections = [choise ? 'asc' : 'desc'];
                      }}
                      .selectables=${[true, false]}
                      .renderString=${(isDesc: boolean) =>
                        this._translator.translate('field', isDesc ? 'sortingAsc' : 'sortingDesc')}
                    ></x-dropdown-selection>
                  </x-flex>
                </x-flex>
              `
            : html`
                <x-flex
                  horizontal
                  space="xs"
                  crossaxis-center
                  @click=${() => {
                    if (userClass === BuddyUserMentee) this._showMenteeSorting = true;
                    else this._showMentorSorting = true;
                  }}
                  ${css({ userSelect: 'none', cursor: 'pointer' })}
                >
                  <x-icon>keyboard_arrow_down</x-icon>
                  <x-text>${this._translator.translate('field', 'sorting')}</x-text>
                </x-flex>
              `}
          <x-hr></x-hr>
        </x-flex>
        <x-drag-drop
          flex
          child-selector="[buddy]"
          allow-from="[buddy]"
          duplicate
          .duplicateFn=${(staticEl, movingEl) => {
            movingEl.user = staticEl.user;
            const staticElBounding = staticEl.getBoundingClientRect();
            const movingElUserDetail = movingEl.querySelector(this._personTogleComponentName);
            if (!movingElUserDetail) return;
            movingElUserDetail.user = staticEl.user;
            movingEl.style.width = staticElBounding.width + 'px';
          }}
        >
          <x-flex h100 w100 space="sm" ${css({ minWidth: '0', padding: '2px' })}>
            ${repeat(users, (user, index) => this._personCard(user, index, onReachEnd))}
          </x-flex>
        </x-drag-drop>
      </x-flex>
    `;
  }

  private _personCard(user: BuddyUser, index: number, onLoad: (index: number) => void) {
    return virtualize(
      html`
        <x-card buddy can-handle padding="sm" .user=${user} ${css({ width: '100%', userSelect: 'none' })}>
          ${this._personToggle(user)}
        </x-card>
      `,
      oldHeight => html` <div ${css({ height: `${oldHeight}px`, gridColumn: '1 / -1' })}></div> `,
      {
        wrapperDisplayContents: true,
        onRender: () => {
          onLoad(index);
        },
      },
    );
  }

  protected virtualizationOnRender(index: number, pagination: SubscriptionPagination<any, any>) {
    if (
      (index === pagination.data.length - 1 || index === pagination.data.length - 1) &&
      pagination.totalRequested.value < index + 80
    ) {
      pagination.next();
    }
  }

  private _createMatchBox() {
    return html`
      <x-drag-drop
        matching
        child-selector="[buddy]"
        dropzone-accept-selector="[buddy]"
        @dropped=${(e: CustomEvent<{ user: BuddyUser }>) => {
          const user = e.detail.user;
          const newMatch = new Match({});
          if (isMentor(user)) {
            newMatch.mentor = user;
            newMatch.mentorID = user._id;
          }
          if (isMentee(user)) {
            newMatch.mentee = user;
            newMatch.menteeID = user._id;
          }
          this._matches.next([newMatch, ...this._matches.value]);
        }}
      >
        <x-flex center crossaxis-center padding="xs" w100>
          <x-text ${css({ color: this.colors.accent })}>
            ${this._translator.translate('buddy', 'dragPersonHere')}
          </x-text>
        </x-flex>
      </x-drag-drop>
    `;
  }

  private _fillMatchBox(match: Match, allow: 'mentor' | 'mentee') {
    return html`
      <x-drag-drop
        matching
        flex
        child-selector="[buddy]"
        dropzone-accept-selector="[buddy]"
        @dropped=${(e: CustomEvent) => {
          const user = e.detail.user as Maybe<BuddyUser>;
          if (!user) return;
          if (
            (user instanceof BuddyUserMentor && allow === 'mentee') ||
            (user instanceof BuddyUserMentee && allow === 'mentor')
          ) {
            return;
          }

          if (isMentor(user)) {
            match.mentorID = user._id;
            match.mentor = user;
          }
          if (isMentee(user)) {
            match.menteeID = user._id;
            match.mentee = user;
          }
          this.requestUpdate();
        }}
        ${css({ height: '100%', minHeight: '80px' })}
      >
        <x-flex center crossaxis-center padding="xs" w100>
          <x-text ${css({ color: this.colors.accent })}>
            ${this._translator.translate('buddy', 'dragPersonHere')}
          </x-text>
        </x-flex>
      </x-drag-drop>
    `;
  }

  protected _personToggle(user: BuddyUser, alwaysOpen?: boolean) {
    return html`
      <buddy-user-details ?always-open=${alwaysOpen} flex ${css({ minWidth: '0' })} .user=${user}></buddy-user-details>
    `;
  }

  protected async _matchUsers(mentors: DefaultMentorUser[], mentees: DefaultMenteeUser[]) {
    return await autoMatchUsers(
      this._currentlySelectedTimeframe.value?._id,
      AutoMatchUserEntry.fromParams(this.menteeUserClass._class, this._menteeFilter),
      AutoMatchUserEntry.fromParams(this.mentorUserClass._class, this._mentorFilter),
    )(selectionSet(() => Match, 3));
  }

  override styles = () => {
    return [
      {
        ':host': { height: '100%' },
        '[matching]': {
          width: '100%',
          height: '80px',
          border: `2px dashed ${this.colors.accent.shade(200)}`,
          borderRadius: this.sizes.borderRadius,
          boxSizing: 'border-box',
        },
        '[matching][dropenabled]': {
          background: this.colors.accent.shade(50),
          border: `2px solid ${this.colors.accent}`,
        },
      },
    ];
  };
}

function isMentor(user: BuddyUser): user is BuddyUserMentor {
  return user instanceof BuddyUserMentor;
}

function isMentee(user: BuddyUser): user is BuddyUserMentee {
  return user instanceof BuddyUserMentee;
}
